/******************************************************************************\
 * Copyright(c) 2021 SAS Institute Inc., Cary, NC, USA. All Rights Reserved.
 *
 * Name: App.tsx
 *
 * Purpose: Initialize the App
 *
 * Author: craig (Craig.Simpson@sas.com)
 *
 * History:
 * ddmmmyyyy userid description (Change Code)
 * 01Sep2021 craig Created.
 \******************************************************************************/
import React, {useState} from 'react';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import {BrowserRouter, Navigate, Outlet, Route, Routes, useLocation} from "react-router-dom";
import {Resources} from "./views/routes/resources/Resources";
import {Admin} from "./views/routes/admin/Admin";
import {IUser, RoleEnum} from "./models/user/IUser";
import {getSessionCookie, GSContext} from "./context/context";
import {Home} from "./views/routes/home/Home";
import {Login} from "./views/routes/forms/login/Login";
import {Dashboard} from "./views/routes/dashboard/Dashboard";
import {Submission} from "./views/routes/submission/Submission";
import {NoMatch} from "./views/routes/nomatch/NoMatch";
import {Team} from "./views/routes/forms/admin/Team";
import {User} from "./views/routes/forms/admin/User";
import {Error403} from "./views/routes/errors/Error403";
import {Attest} from "./views/routes/attest/Attest";
import {Layout} from "./Layout";
import {ResetPassword} from "./views/routes/forms/reset/ResetPassword";
import {ForgotPassword} from "./views/routes/forms/forgot/ForgotPassword";
import {Error500} from "./views/routes/errors/Error500";
import {Guide} from "./views/routes/forms/guide/Guide";
import {System} from "./views/routes/admin/System";
import {SystemProjectJson} from "./views/routes/admin/SystemProjectJson";
import {TeamComments} from "./views/routes/comments/TeamComments";

const App: React.FC = () => {

  const [sessionUser, setSessionUser] = useState<IUser>(getSessionCookie());


  const NonAuthRoute = ({redirectPath = '/'}) => {
    if (sessionUser.UserID > 0) {
      return <Navigate to={redirectPath} replace/>;
    }
    return <Outlet/>;
  }

  const ProtectedRoute = ({redirectPath = '/login'}, {attestPath = '/attest'}) => {
    const location = useLocation();
    if (sessionUser.UserID < 0) {
      return <Navigate to={redirectPath} replace state={location}/>;
    } else if (sessionUser.Attested === false) {
      return <Navigate to={attestPath} replace state={location}/>;
    }
    return <Outlet/>;
  };

  const ProtectedAdminRoute = ({redirectPath = '/error/403'}) => {
    if (sessionUser.UserID < 0 || sessionUser.RoleID !== RoleEnum.Admin) {
      return <Navigate to={redirectPath} replace/>;
    }
    return <Outlet/>;
  };

  const ProtectedGSAdminRoute = ({redirectPath = '/error/403'}) => {
    if (sessionUser.UserID < 0 || (sessionUser.RoleID !== RoleEnum.Admin && sessionUser.RoleID !== RoleEnum.GS_Admin)) {
      return <Navigate to={redirectPath} replace/>;
    }
    return <Outlet/>;
  }

  return (
      <GSContext.Provider value={[sessionUser, setSessionUser]}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Layout/>}>
              <Route path={"/"} element={<Home/>}/>
              <Route element={<NonAuthRoute/>}>
                <Route path={"/login"} element={<Login/>}/>
                <Route path={"/forgot"} element={<ForgotPassword/>}/>
                <Route path={"/reset/:t/:d"} element={<ResetPassword/>}/>
              </Route>
              <Route path={"attest"} element={<Attest/>}/>
              <Route element={<ProtectedRoute/>}>
                <Route path="/dashboard" element={<Dashboard/>}/>
                <Route path="/submission/:userID/:teamID/:submissionID/:activePillar"
                       element={<Submission/>}/>
                <Route path="/resources" element={<Resources/>}/>
                <Route path={"/guide"} element={<Guide/>}/>
                <Route path={"/teamComments/:teamID"} element={<TeamComments/>}/>
              </Route>
              <Route element={<ProtectedGSAdminRoute/>}>
                <Route path="/admin" element={<Admin/>}>
                  <Route path={":adminSection"} element={<Admin/>}/>
                </Route>
                <Route path="/admin/team" element={<Team/>}/>
                <Route path="/admin/team/:teamID" element={<Team/>}/>
                <Route path="/admin/user" element={<User/>}/>
                <Route path="/admin/user/:userID" element={<User/>}/>
              </Route>
              <Route element={<ProtectedAdminRoute/>}>
                <Route path="/admin/system/links" element={<System/>}/>
                <Route path="/admin/system/processjson/:days" element={<SystemProjectJson/>}/>
              </Route>
              <Route path={"*"} element={<NoMatch/>}/>
              <Route path={"/error/403"} element={<Error403/>}/>
              <Route path={"/error/500"} element={<Error500/>}/>
            </Route>
          </Routes>
        </BrowserRouter>
      </GSContext.Provider>
  );
}

export default App;
